import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { createLegacyClientImport } from "@/lib/legacyClientsApi";
import { useToast } from "@/hooks/use-toast";
import { Upload, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";

interface JsonClient {
  id: number;
  first_name: string;
  last_name: string;
  company: string;
  group_name: string;
  operator_agency_name?: string;
  operator_agent_name?: string;
  email: string;
  mobile: string;
  alternate_phone: string;
  referred_from: string;
  language: string;
  currency: string;
}

const ImportClients = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<{ success: number; skipped: number; errors: number } | null>(null);
  const { userRole } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const canImport = userRole === "ceo" || userRole === "general_manager";

  useEffect(() => {
    if (!canImport) {
      navigate("/partners");
    }
  }, [canImport, navigate]);

  if (!canImport) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to import clients.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const cleanValue = (value: string): string | null => {
    if (!value || value === "" || value === "-" || value === "0" || value.toLowerCase() === "null") {
      return null;
    }
    return value.trim();
  };

  const mapReferredFrom = (value: string): string | null => {
    const cleaned = cleanValue(value);
    if (!cleaned) return null;
    
    const validValues = ["instagram", "facebook", "google", "referral", "website", "other"];
    const lowerValue = cleaned.toLowerCase();
    
    if (validValues.includes(lowerValue)) {
      return lowerValue;
    }
    
    return "other";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "application/json") {
      setFile(selectedFile);
      setResults(null);
    } else {
      toast({
        title: "Invalid file",
        description: "Please select a valid JSON file",
        variant: "destructive",
      });
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setProgress(0);
    setResults(null);

    try {
      const fileContent = await file.text();
      const jsonData: JsonClient[] = JSON.parse(fileContent);

      let success = 0;
      const skipped = 0;
      let errors = 0;

      for (let i = 0; i < jsonData.length; i++) {
        const client = jsonData[i];

        try {
          const validLanguages = ["en", "pt_br"];
          const language = validLanguages.includes(client.language) 
            ? client.language 
            : "en";
          
          const validCurrencies = ["usd", "aed"];
          const currency = validCurrencies.includes(client.currency) 
            ? client.currency 
            : "usd";

          const clientData = {
            id: client.id,
            first_name: cleanValue(client.first_name),
            last_name: cleanValue(client.last_name),
            company: cleanValue(client.company),
            group_name: cleanValue(client.group_name),
            email: cleanValue(client.email),
            mobile: cleanValue(client.mobile),
            alternate_phone: cleanValue(client.alternate_phone),
            operator_agency_name: cleanValue(client.operator_agency_name || client.operator_agent_name || ""),
            referred_from: mapReferredFrom(client.referred_from) as "instagram" | "facebook" | "google" | "referral" | "website" | "other" | null,
            language: language as "en" | "pt_br",
            currency: currency as "usd" | "aed",
          };

          await createLegacyClientImport(clientData);
          success++;
        } catch (err) {
          console.error("Error processing client:", err);
          errors++;
        }

        setProgress(((i + 1) / jsonData.length) * 100);
      }

      setResults({ success, skipped, errors });
      
      toast({
        title: "Import completed",
        description: `${success} clients imported successfully. ${errors} errors.`,
      });
    } catch (error) {
      console.error("Error importing clients:", error);
      toast({
        title: "Import failed",
        description: "Failed to read or parse the JSON file",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-3xl font-bold">Import Clients</h1>
          <p className="text-muted-foreground">
            Upload a JSON file to bulk import legacy client contacts from the old system
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Upload JSON File</CardTitle>
            <CardDescription>
              Select a JSON file containing legacy client data from the previous system.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file">JSON File</Label>
              <Input
                id="file"
                type="file"
                accept=".json"
                onChange={handleFileChange}
                disabled={importing}
              />
            </div>

            {file && (
              <div className="text-sm text-muted-foreground">
                Selected file: {file.name}
              </div>
            )}

            {importing && (
              <div className="space-y-2">
                <Progress value={progress} />
                <p className="text-sm text-muted-foreground text-center">
                  Importing... {Math.round(progress)}%
                </p>
              </div>
            )}

            {results && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p><strong>Import Results:</strong></p>
                    <p>✓ Successfully imported: {results.success}</p>
                    <p>✗ Errors: {results.errors}</p>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleImport}
                disabled={!file || importing}
                className="flex-1"
              >
                <Upload className="mr-2 h-4 w-4" />
                {importing ? "Importing..." : "Import Clients"}
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate("/clients")}
                disabled={importing}
              >
                Back to Clients
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ImportClients;
